import { Injectable, ElementRef, ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CellContextMenuEvent } from 'ag-grid-community';
import { Clipboard } from '@angular/cdk/clipboard';

@Injectable({
  providedIn: 'root',
})
export class ContextMenuService {
  private menuState = new BehaviorSubject<{
    showMenu: boolean;
    menuTop: number;
    menuLeft: number;
    cellValue: string | null;
    colId?: string | null;
    rowData?: any | null;
  }>({
    showMenu: false,
    menuTop: 0,
    menuLeft: 0,
    cellValue: null,
    colId: null,
    rowData: null,
  });
  menuState$ = this.menuState.asObservable();

  private isHoveringMenu = false;
  private isHoveringGrid = false;
  private cdr: ChangeDetectorRef | null = null;
  private gridElement: ElementRef | null = null;

  // Approximate rendered size of the context menu, used to keep it on-screen.
  private readonly MENU_WIDTH = 210;
  private readonly MENU_HEIGHT = 140;

  constructor(private clipboard: Clipboard) {}

  // Compute a menu position that stays within the viewport. If the menu would
  // overflow the right/bottom edge it flips to the other side of the cursor,
  // then is clamped so it is never cut off (e.g. at shorter viewports like 824px).
  private clampMenuPosition(
    clientX: number,
    clientY: number,
    offset = 10,
  ): { top: number; left: number } {
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = clientX + offset;
    let top = clientY + offset;

    if (left + this.MENU_WIDTH + margin > vw) {
      left = clientX - this.MENU_WIDTH - offset;
    }
    if (top + this.MENU_HEIGHT + margin > vh) {
      top = clientY - this.MENU_HEIGHT - offset;
    }

    left = Math.max(margin, Math.min(left, vw - this.MENU_WIDTH - margin));
    top = Math.max(margin, Math.min(top, vh - this.MENU_HEIGHT - margin));

    return { top, left };
  }

  // Initialize service with component's ChangeDetectorRef and grid ElementRef
  initialize(cdr: ChangeDetectorRef, gridElement: ElementRef) {
    this.cdr = cdr;
    this.gridElement = gridElement;
    this.setupGridContextMenuListener();
  }

  // Set up grid-level contextmenu listener to suppress browser menu
  private setupGridContextMenuListener(): (() => void) | undefined {
    const element = this.gridElement?.nativeElement;
    if (element) {
      const handler = (e: MouseEvent) => {
        e.preventDefault();
        // Let AG Grid's cellContextMenu event provide cell value for cell clicks.
        if ((e.target as HTMLElement)?.closest('.ag-cell')) {
          return;
        }
        if (!this.menuState.getValue().showMenu) {
          const pos = this.clampMenuPosition(e.clientX, e.clientY, 10);
          this.menuState.next({
            showMenu: true,
            menuTop: pos.top,
            menuLeft: pos.left,
            cellValue: null,
            colId: null,
            rowData: null,
          });
          this.isHoveringGrid = true;
          this.cdr?.detectChanges();
          this.addScrollListener();
          this.setupGridMouseLeave();
        }
      };
      element.addEventListener('contextmenu', handler);
      return () => element.removeEventListener('contextmenu', handler);
    }
    return undefined; // Explicit return for when element is not available
  }

  // Handle AG Grid cellContextMenu event
  onCellContextMenu(event: CellContextMenuEvent) {
    const cellValue = event.value?.toString() || '';
    const colId = event.column?.getColId() || null;
    const rowData = event.data || null;
    const pos =
      event.event instanceof MouseEvent
        ? this.clampMenuPosition(event.event.clientX, event.event.clientY, 10)
        : { top: 0, left: 0 };

    this.menuState.next({
      showMenu: true,
      menuTop: pos.top,
      menuLeft: pos.left,
      cellValue,
      colId,
      rowData,
    });
    this.isHoveringGrid = true;
    this.cdr?.detectChanges();
    this.addScrollListener();

    if (event.event) {
      event.event.preventDefault();
    }
    this.setupGridMouseLeave();
  }

  // Handle manual right-click event
  onRightClick(event: MouseEvent, cellValue: string | null = null) {
    event.preventDefault();
    const pos = this.clampMenuPosition(event.clientX, event.clientY, 5);

    this.menuState.next({
      showMenu: true,
      menuTop: pos.top,
      menuLeft: pos.left,
      cellValue,
      colId: null,
      rowData: null,
    });
    this.isHoveringGrid = true;
    this.cdr?.detectChanges();
    this.addScrollListener();
    this.setupGridMouseLeave();
  }

  // Set up mouseleave listener for grid
  private setupGridMouseLeave() {
    const element = this.gridElement?.nativeElement;
    if (element) {
      const mouseLeaveHandler = () => {
        setTimeout(() => {
          const mouseNearMenu = this.isMouseNearMenu();
          if (!mouseNearMenu) {
            this.isHoveringGrid = false;
            this.checkIfShouldCloseMenu();
          }
        }, 200);
      };
      element.addEventListener('mouseleave', mouseLeaveHandler, { once: true });

      const cleanup = () => {
        element.removeEventListener('mouseleave', mouseLeaveHandler);
        document.removeEventListener('click', cleanup);
      };
      document.addEventListener('click', cleanup, { once: true });
    } else {
      const clickHandler = () => {
        this.closeMenu();
        document.removeEventListener('click', clickHandler);
      };
      document.addEventListener('click', clickHandler, { once: true });
    }
  }

  // Check if mouse is near menu
  private isMouseNearMenu(): boolean {
    let mouseX = 0;
    let mouseY = 0;
    const updateMousePosition = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    document.addEventListener('mousemove', updateMousePosition, { once: true });

    const { menuTop, menuLeft } = this.menuState.getValue();
    const menuWidth = 100;
    const menuHeight = 30;
    const buffer = 50;
    const nearMenu =
      mouseX >= menuLeft - buffer &&
      mouseX <= menuLeft + menuWidth + buffer &&
      mouseY >= menuTop - buffer &&
      mouseY <= menuTop + menuHeight + buffer;

    document.removeEventListener('mousemove', updateMousePosition);
    return nearMenu;
  }

  // Handle menu mouse enter
  onMenuMouseEnter() {
    this.isHoveringMenu = true;
  }

  // Handle menu mouse leave
  onMenuMouseLeave() {
    this.isHoveringMenu = false;
    this.checkIfShouldCloseMenu();
  }

  // Check if menu should close
  private checkIfShouldCloseMenu() {
    if (!this.isHoveringMenu && !this.isHoveringGrid) {
      this.closeMenu();
    }
  }

  // Copy cell value to clipboard
  copyCellValue() {
    const { cellValue } = this.menuState.getValue();
    if (cellValue !== null && cellValue !== undefined) {
      this.clipboard.copy(cellValue);
      this.closeMenu();
    }
  }

  private scrollHandler = () => this.closeMenu();

  private addScrollListener() {
    window.addEventListener('scroll', this.scrollHandler, true);
  }

  private removeScrollListener() {
    window.removeEventListener('scroll', this.scrollHandler, true);
  }

  // Close menu and update state
  private closeMenu() {
    this.menuState.next({
      ...this.menuState.getValue(),
      showMenu: false,
    });
    this.isHoveringMenu = false;
    this.isHoveringGrid = false;
    this.removeScrollListener();
    this.cdr?.detectChanges();
  }
}
